import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useNavigate, Link } from "react-router-dom";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaRupeeSign,
  FaArrowLeft,
  FaCrown,
  FaHistory,
  FaExclamationTriangle,
  FaCalendarCheck,
  FaCalendarTimes,
  FaTrash,
  FaEye,
  FaHourglassHalf,
  FaHourglassEnd,
  FaCreditCard,
  FaFileInvoiceDollar,
  FaInfoCircle,
} from "react-icons/fa";

const Membership = () => {
  const server_url = localStorage.getItem("server_url");
  const garage_id = localStorage.getItem("grage_id");
  const garage_name = localStorage.getItem("grage_name");
  const navigate = useNavigate();

  const [activeMembership, setActiveMembership] = useState([]);
  const [waitingMemberships, setWaitingMemberships] = useState([]);
  const [pendingMemberships, setPendingMemberships] = useState([]);
  const [expiredMemberships, setExpiredMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    fetchAllMemberships();
  }, []);

  const fetchAllMemberships = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("garage_id", garage_id);

      const response = await axios.post(
        `${server_url}get_membership.php`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );


      if (response.data.status === "1") {
        setActiveMembership(response.data.result_active || []);
        setWaitingMemberships(response.data.result_waiting || []);
        setPendingMemberships(response.data.result_pending || []);
        setExpiredMemberships(response.data.result_expired || []);

        // Set current status for local storage
        if (
          response.data.result_active &&
          response.data.result_active.length > 0
        ) {
          localStorage.setItem("membership_status", "active");
          localStorage.setItem(
            "current_membership_id",
            response.data.result_active[0].id
          );
        } else {
          localStorage.setItem("membership_status", "inactive");
        }
      } else {
        toast.error(response.data.error || "Failed to fetch memberships");
        setActiveMembership([]);
        setWaitingMemberships([]);
        setPendingMemberships([]);
        setExpiredMemberships([]);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Unable to load membership details");
      setActiveMembership([]);
      setWaitingMemberships([]);
      setPendingMemberships([]);
      setExpiredMemberships([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelMembership = async () => {
    if (!selectedMembership) return;

    setCancelling(true);
    try {
      const formData = new FormData();
      formData.append("membership_id", selectedMembership.id);
      formData.append("garage_id", garage_id);

      const response = await axios.post(
        `${server_url}cancel_membership.php`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.data.status === "1") {
        toast.success("Membership cancelled successfully");
        setShowCancelModal(false);
        fetchAllMemberships(); // Refresh all membership data
      } else {
        toast.error(response.data.error || "Failed to cancel membership");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to cancel membership");
    } finally {
      setCancelling(false);
    }
  };

  const showMembershipDetails = (membership) => {
    setSelectedMembership(membership);
    setShowDetailsModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return { bg: "#10B981", text: "white", icon: <FaCheckCircle /> };
      case "waiting":
        return { bg: "#F59E0B", text: "white", icon: <FaHourglassHalf /> };
      case "pending":
        return { bg: "#3B82F6", text: "white", icon: <FaCreditCard /> };
      case "expired":
        return { bg: "#EF4444", text: "white", icon: <FaTimesCircle /> };
      default:
        return { bg: "#6B7280", text: "white", icon: <FaClock /> };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Cancel Membership Modal
  const CancelMembershipModal = () => {
    return (
      <div
        style={styles.modalOverlay}
        onClick={() => !cancelling && setShowCancelModal(false)}
      >
        <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>Cancel Membership</h2>
          </div>
          <div style={styles.modalBody}>
            <div style={styles.warningIcon}>
              <FaExclamationTriangle size={40} color="#f59e0b" />
            </div>
            <p style={styles.modalMessage}>
              Are you sure you want to cancel this membership?{" "}
              <b>This action cannot be reversed.</b>
            </p>
            <div style={styles.modalButtons}>
              <button
                style={styles.secondaryButton}
                onClick={() => setShowCancelModal(false)}
                disabled={cancelling}
              >
                Cancel
              </button>
              <button
                style={{
                  ...styles.dangerButton,
                  ...(cancelling && styles.disabledButton),
                }}
                onClick={handleCancelMembership}
                disabled={cancelling}
              >
                {cancelling ? "Cancelling..." : "Cancel Membership"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Membership Details Modal (for non-active memberships)
  const MembershipDetailsModal = () => {
    if (!selectedMembership) return null;

    const statusInfo = getStatusColor(selectedMembership.membership_status);
    const isActive = selectedMembership.membership_status === "active";
    const isWaiting = selectedMembership.membership_status === "waiting";
    const isPending = selectedMembership.membership_status === "pending";

    return (
      <div
        style={styles.modalOverlay}
        onClick={() => setShowDetailsModal(false)}
      >
        <div
          style={styles.modalContentLarge}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>Membership Details</h2>
            <button
              style={styles.modalClose}
              onClick={() => setShowDetailsModal(false)}
            >
              &times;
            </button>
          </div>

          <div style={styles.modalBody}>
            {/* Status Badge */}
            <div style={styles.detailStatusBadge}>
              <span
                style={{
                  backgroundColor: statusInfo.bg,
                  color: statusInfo.text,
                  padding: "6px 12px",
                  borderRadius: "20px",
                  fontSize: "14px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                {statusInfo.icon}
                {selectedMembership.membership_status.toUpperCase()}
              </span>
            </div>

            {/* Package Info */}
            <div style={styles.detailSection}>
              <h3 style={styles.detailSectionTitle}>Package Information</h3>
              <div style={styles.detailGrid}>
                <div className="width160" style={styles.detailItem}>
                  <span style={styles.detailLabel}>Package Name:</span>
                  <span style={styles.detailValue}>
                    {selectedMembership.package_name}
                  </span>
                </div>
                <div className="textright" style={styles.detailItem}>
                  <span style={styles.detailLabel}>Duration:</span>
                  <span style={styles.detailValue}>
                    {selectedMembership.package_days} days
                  </span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Price:</span>
                  <span style={styles.detailValue}>
                    ₹{selectedMembership.package_price}
                  </span>
                </div>
              </div>
            </div>

            {/* Dates Section */}
            <div style={styles.detailSection}>
              <h3 style={styles.detailSectionTitle}>Membership Period</h3>
              <div style={styles.detailGrid}>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Start Date:</span>
                  <span style={styles.detailValue}>
                    {formatDate(selectedMembership.start_date)}
                  </span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>End Date:</span>
                  <span style={styles.detailValue}>
                    {formatDate(selectedMembership.end_date)}
                  </span>
                </div>
                {isActive && (
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Days Remaining:</span>
                    <span style={styles.detailValue}>
                      {selectedMembership.days_remaining} days
                      {selectedMembership.is_expiring_soon &&
                        " (Expiring soon)"}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Payment Information */}
            <div style={styles.detailSection}>
              <h3 style={styles.detailSectionTitle}>Payment Information</h3>
              <div style={styles.detailGrid}>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Payment Status:</span>
                  <span style={styles.detailValue}>
                    <span
                      style={{
                        color:
                          selectedMembership.payment_status === "success"
                            ? "#10B981"
                            : "#EF4444",
                        fontWeight: "600",
                      }}
                    >
                      {selectedMembership.payment_status === "success"
                        ? "PAID"
                        : "PENDING"}
                    </span>
                  </span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Payment Date:</span>
                  <span style={styles.detailValue}>
                    {selectedMembership.payment_date_formatted ||
                      formatDateTime(selectedMembership.payment_date)}
                  </span>
                </div>
                {/* {selectedMembership.razorpay_order_id && (
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Order ID:</span>
                    <span style={styles.detailValue}>
                      {selectedMembership.razorpay_order_id}
                    </span>
                  </div>
                )} */}
              </div>
            </div>

            {/* Usage Information (Active only) */}
            {isActive && (
              <div style={styles.detailSection}>
                <h3 style={styles.detailSectionTitle}>Usage Information</h3>
                <div style={styles.detailGrid}>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Days Used:</span>
                    <span style={styles.detailValue}>
                      {selectedMembership.days_used} days
                    </span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Usage Percentage:</span>
                    <span style={styles.detailValue}>
                      {selectedMembership.usage_percentage}%
                    </span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Progress:</span>
                    <div style={styles.progressBarSmall}>
                      <div
                        style={{
                          ...styles.progressFillSmall,
                          width: `${Math.min(
                            selectedMembership.usage_percentage,
                            100
                          )}%`,
                          backgroundColor: selectedMembership.is_expiring_soon
                            ? "#F59E0B"
                            : "#10B981",
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={styles.detailActions}>
              {isActive && (
                <button
                  style={styles.cancelButton}
                  onClick={() => {
                    setShowDetailsModal(false);
                    setShowCancelModal(true);
                  }}
                >
                  <FaTrash /> Cancel Membership
                </button>
              )}
              {isPending && (
                <button
                  style={styles.payButton}
                  onClick={() => {
                    setShowDetailsModal(false);
                    // Handle payment for pending membership
                    toast.info("Redirecting to payment...");
                  }}
                >
                  <FaCreditCard /> Complete Payment
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Small Card for non-active memberships
  const renderMembershipCard = (membership, index) => {
    const statusInfo = getStatusColor(membership.membership_status);

    return (
      <div key={index} style={styles.membershipCardSmall}>
        <div style={styles.cardHeaderSmall}>
          <div style={styles.statusBadgeSmall}>
            <span
              style={{
                backgroundColor: statusInfo.bg,
                color: statusInfo.text,
                padding: "4px 8px",
                borderRadius: "12px",
                fontSize: "12px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              {statusInfo.icon}
              {membership.membership_status}
            </span>
          </div>
          <h3 style={styles.packageNameSmall}>{membership.package_name}</h3>
          <div style={styles.priceSectionSmall}>
            <FaRupeeSign size={16} />
            <span style={styles.priceSmall}>{membership.package_price}</span>
          </div>
        </div>

        <div style={styles.cardBodySmall}>
          <div style={styles.detailRow}>
            <FaCalendarAlt size={12} />
            <span style={styles.detailText}>
              {formatDate(membership.start_date)} -{" "}
              {formatDate(membership.end_date)}
            </span>
          </div>
          <div style={styles.detailRow}>
            <FaClock size={12} />
            <span style={styles.detailText}>
              {membership.package_days} days
            </span>
          </div>
        </div>

        <div style={styles.cardFooterSmall}>
          <button
            style={styles.viewDetailsButton}
            onClick={() => showMembershipDetails(membership)}
          >
            <FaEye /> View Details
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={styles.spinner}></div>
          <p>Loading membership details...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Modals */}
      {showCancelModal && <CancelMembershipModal />}
      {showDetailsModal && <MembershipDetailsModal />}
      {/* Active Memberships Section - Show Full Details */}
      {activeMembership.length > 0 &&
        activeMembership.map((membership, index) => (
          <div key={index} style={styles.membershipCard}>
            <div style={styles.cardHeader}>
              <div style={styles.statusBadge}>
                <span
                  style={{
                    backgroundColor: "#10B981",
                    color: "white",
                    padding: "6px 12px",
                    borderRadius: "20px",
                    fontSize: "14px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <FaCrown size={12} /> Active
                </span>
              </div>
              <h2 style={styles.packageName}>{membership.package_name}</h2>
              <div style={styles.priceSection}>
                <FaRupeeSign size={20} />
                <span style={styles.price}>{membership.package_price}</span>
              </div>
            </div>

            {/* Progress Bar */}
            <div style={styles.progressSection}>
              <div style={styles.progressHeader}>
                <span style={styles.progressLabel}>Membership Progress</span>
                <span style={styles.daysInfo}>
                  {membership.days_used} of {membership.package_days} days used
                </span>
              </div>
              <div style={styles.progressBar}>
                <div
                  style={{
                    ...styles.progressFill,
                    width: `${Math.min(membership.usage_percentage, 100)}%`,
                    backgroundColor: membership.is_expiring_soon
                      ? "#F59E0B"
                      : "#10B981",
                  }}
                ></div>
              </div>
              <div style={styles.daysRemaining}>
                <FaClock size={14} />
                <span style={styles.daysText}>
                  {membership.days_remaining} days remaining
                  {membership.is_expiring_soon && (
                    <span style={styles.warningText}> (Expiring soon)</span>
                  )}
                </span>
              </div>
            </div>

            {/* Details Grid */}
            <div style={styles.detailsGrid}>
              <div style={styles.detailItem}>
                <div style={styles.detailIcon}>
                  <FaCalendarAlt />
                </div>
                <div>
                  <div style={styles.detailLabel}>Start Date</div>
                  <div style={styles.detailValue}>
                    {formatDate(membership.start_date)}
                  </div>
                </div>
              </div>

              <div style={styles.detailItem}>
                <div style={styles.detailIcon}>
                  <FaCalendarCheck />
                </div>
                <div>
                  <div style={styles.detailLabel}>End Date</div>
                  <div style={styles.detailValue}>
                    {formatDate(membership.end_date)}
                  </div>
                </div>
              </div>

              <div style={styles.detailItem}>
                <div style={styles.detailIcon}>
                  <FaClock />
                </div>
                <div>
                  <div style={styles.detailLabel}>Duration</div>
                  <div style={styles.detailValue}>
                    {membership.package_days} days
                  </div>
                </div>
              </div>

              <div style={styles.detailItem}>
                <div style={styles.detailIcon}>
                  <FaCheckCircle />
                </div>
                <div>
                  <div style={styles.detailLabel}>Payment Status</div>
                  <div style={styles.detailValue}>
                    <span
                      style={{
                        color: "#10B981",
                        fontWeight: "600",
                      }}
                    >
                      Paid
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={styles.actionButtons}>
              <button
                style={styles.cancelButton}
                onClick={() => {
                  setSelectedMembership(membership);
                  setShowCancelModal(true);
                }}
              >
                <FaTrash /> Cancel Membership
              </button>
            </div>
          </div>
        ))}

      {/* Waiting Memberships Section - Show in Cards */}
      {waitingMemberships.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              <FaHourglassHalf style={{ marginRight: 8 }} /> Waiting Memberships
            </h2>
            <span style={styles.sectionInfo}>
              {waitingMemberships.length} in queue
            </span>
          </div>
          <div style={styles.cardsContainer}>
            {waitingMemberships.map((membership, index) =>
              renderMembershipCard(membership, index)
            )}
          </div>
        </div>
      )}

      {/* Pending Memberships Section - Show in Cards */}
      {pendingMemberships.length > 0 && 1 == 2 && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              <FaCreditCard style={{ marginRight: 8 }} /> Pending Memberships
            </h2>
            <span style={styles.sectionInfo}>Payment required</span>
          </div>
          <div style={styles.cardsContainer}>
            {pendingMemberships.map((membership, index) =>
              renderMembershipCard(membership, index)
            )}
          </div>
        </div>
      )}

      {/* Expired Memberships Section - Show in Cards */}
      {expiredMemberships.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              <FaTimesCircle style={{ marginRight: 8 }} /> Expired Memberships
            </h2>
            <span style={styles.sectionInfo}>
              {expiredMemberships.length} expired
            </span>
          </div>
          <div style={styles.cardsContainer}>
            {expiredMemberships.map((membership, index) =>
              renderMembershipCard(membership, index)
            )}
          </div>
        </div>
      )}

      {/* No Memberships Found */}
      {activeMembership.length === 0 &&
        waitingMemberships.length === 0 &&
        pendingMemberships.length === 0 &&
        expiredMemberships.length === 0 && (
          <div style={styles.noMembershipCard}>
            <div style={styles.noMembershipIcon}>
              <FaExclamationTriangle size={40} color="#F59E0B" />
            </div>
            <h2 style={styles.noMembershipTitle}>No Memberships Found</h2>
            <p style={styles.noMembershipText}>
              You don't have any membership plans. Subscribe to a package to
              unlock all features.
            </p>
            <button
              style={styles.subscribeButton}
              onClick={() => navigate("/PackageList")}
            >
              <FaCrown /> Browse Packages
            </button>
          </div>
        )}

        <style jsx>
  {`
    .textright {
      text-align: right !important;
    }
    .width160 {
      width: 160%;
    }
  `}</style>

      {/* Footer Info */}
    </div>
  );
};

// Styles
const styles = {
  container: {
    padding: "16px",
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
    fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
    flexWrap: "wrap",
    gap: "12px",
  },
  backButton: {
    background: "none",
    border: "none",
    fontSize: "16px",
    color: "#00409e",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontWeight: "500",
  },
  title: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#00409e",
    margin: 0,
  },
  headerActions: {
    display: "flex",
    gap: "12px",
  },
  browseButton: {
    padding: "10px 16px",
    backgroundColor: "#00409e",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  loading: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "50vh",
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "3px solid #f3f3f3",
    borderTop: "3px solid #00409e",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginBottom: "16px",
  },

  // Full Details Membership Card (for active)
  membershipCard: {
    backgroundColor: "white",
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    marginBottom: "20px",
  },
  cardHeader: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "24px",
    textAlign: "center",
  },
  statusBadge: {
    marginBottom: "12px",
  },
  packageName: {
    fontSize: "22px",
    fontWeight: "600",
    color: "#1f2937",
    margin: "0 0 12px 0",
  },
  priceSection: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    color: "#00409e",
  },
  price: {
    fontSize: "36px",
    fontWeight: "700",
  },
  progressSection: {
    marginBottom: "24px",
  },
  progressHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  progressLabel: {
    fontSize: "14px",
    color: "#6b7280",
    fontWeight: "500",
  },
  daysInfo: {
    fontSize: "14px",
    color: "#374151",
    fontWeight: "600",
  },
  progressBar: {
    height: "8px",
    backgroundColor: "#e5e7eb",
    borderRadius: "4px",
    overflow: "hidden",
    marginBottom: "8px",
  },
  progressFill: {
    height: "100%",
    borderRadius: "4px",
    transition: "width 0.3s ease",
  },
  daysRemaining: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "14px",
    color: "#374151",
  },
  warningText: {
    color: "#f59e0b",
    fontWeight: "600",
  },
  detailsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "16px",
    marginBottom: "24px",
  },
  detailItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px",
    backgroundColor: "#f9fafb",
    borderRadius: "8px",
  },
  detailIcon: {
    width: "36px",
    height: "36px",
    backgroundColor: "#e0e7ff",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#00409e",
    fontSize: "16px",
  },
  detailLabel: {
    fontSize: "12px",
    color: "#6b7280",
    marginBottom: "2px",
  },
  detailValue: {
    fontSize: "14px",
    color: "#1f2937",
    fontWeight: "600",
  },
  actionButtons: {
    display: "flex",
    gap: "12px",
    marginTop: "24px",
  },
  cancelButton: {
    flex: 1,
    padding: "14px",
    backgroundColor: "#fff",
    color: "#dc2626",
    border: "2px solid #dc2626",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    transition: "all 0.3s ease",
  },

  // Sections for other statuses
  section: {
    marginBottom: "24px",
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1f2937",
    margin: 0,
    display: "flex",
    alignItems: "center",
  },
  sectionInfo: {
    fontSize: "12px",
    color: "#6b7280",
    backgroundColor: "#f3f4f6",
    padding: "4px 8px",
    borderRadius: "12px",
  },

  // Cards Container for non-active memberships
  cardsContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "16px",
  },

  // Small Membership Card for non-active
  membershipCardSmall: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "16px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    display: "flex",
    flexDirection: "column",
  },
  cardHeaderSmall: {
    marginBottom: "12px",
  },
  statusBadgeSmall: {
    marginBottom: "8px",
  },
  packageNameSmall: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1f2937",
    margin: "0 0 8px 0",
  },
  priceSectionSmall: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    color: "#00409e",
  },
  priceSmall: {
    fontSize: "20px",
    fontWeight: "700",
  },
  cardBodySmall: {
    flex: 1,
    marginBottom: "12px",
  },
  detailRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "6px",
    fontSize: "12px",
    color: "#6b7280",
  },
  detailText: {
    fontSize: "12px",
    color: "#374151",
  },
  cardFooterSmall: {
    marginTop: "auto",
  },
  viewDetailsButton: {
    width: "100%",
    padding: "8px",
    backgroundColor: "#f3f4f6",
    color: "#374151",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "500",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    transition: "all 0.2s",
  },

  // No Membership Card
  noMembershipCard: {
    backgroundColor: "white",
    borderRadius: "16px",
    padding: "32px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    textAlign: "center",
    marginBottom: "20px",
  },
  noMembershipIcon: {
    marginBottom: "16px",
  },
  noMembershipTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#1f2937",
    margin: "0 0 8px 0",
  },
  noMembershipText: {
    fontSize: "16px",
    color: "#6b7280",
    marginBottom: "24px",
    lineHeight: "1.5",
  },
  subscribeButton: {
    padding: "16px 32px",
    backgroundColor: "#00409e",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    margin: "0 auto",
    transition: "all 0.3s ease",
  },

  // Modals
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "16px",
  },
  modalContent: {
    backgroundColor: "white",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "400px",
    overflow: "hidden",
  },
  modalContentLarge: {
    backgroundColor: "white",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "500px",
    maxHeight: "90vh",
    overflow: "auto",
  },
  modalHeader: {
    padding: "20px 20px 10px",
    textAlign: "center",
    position: "relative",
  },
  modalTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#00409e",
    margin: 0,
  },
  modalClose: {
    position: "absolute",
    right: "20px",
    top: "20px",
    background: "none",
    border: "none",
    fontSize: "24px",
    color: "#6b7280",
    cursor: "pointer",
    width: "30px",
    height: "30px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalBody: {
    padding: "10px 20px 20px",
  },
  warningIcon: {
    textAlign: "center",
    marginBottom: "16px",
  },
  modalMessage: {
    fontSize: "14px",
    color: "#666",
    lineHeight: "1.5",
    textAlign: "center",
    marginBottom: "20px",
  },
  modalButtons: {
    display: "flex",
    gap: "10px",
  },
  secondaryButton: {
    flex: 1,
    padding: "12px",
    backgroundColor: "#f8f9fa",
    color: "#495057",
    border: "1px solid #dee2e6",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  dangerButton: {
    flex: 1,
    padding: "12px",
    backgroundColor: "#dc2626",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },
  disabledButton: {
    opacity: 0.6,
    cursor: "not-allowed",
  },

  // Details Modal Styles
  detailStatusBadge: {
    textAlign: "center",
    marginBottom: "20px",
  },
  detailSection: {
    marginBottom: "20px",
    paddingBottom: "20px",
    borderBottom: "1px solid #e5e7eb",
  },
  detailSectionTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#374151",
    margin: "0 0 12px 0",
  },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "12px",
  },
  detailItem: {
    display: "flex",
    flexDirection: "column",
  },
  detailLabel: {
    fontSize: "12px",
    color: "#6b7280",
    marginBottom: "4px",
  },
  detailValue: {
    fontSize: "14px",
    color: "#1f2937",
    fontWeight: "500",
  },
  detailActions: {
    display: "flex",
    gap: "12px",
    marginTop: "24px",
  },
  payButton: {
    flex: 1,
    padding: "12px",
    backgroundColor: "#10B981",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  infoButton: {
    flex: 1,
    padding: "12px",
    backgroundColor: "#3B82F6",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },

  // Footer
  footer: {
    marginTop: "30px",
    paddingTop: "20px",
    borderTop: "1px solid #e5e7eb",
    textAlign: "center",
  },
  footerText: {
    fontSize: "12px",
    color: "#6b7280",
    margin: "4px 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
  },
};

// Add CSS animations
if (typeof document !== "undefined" && document.styleSheets.length > 0) {
  const styleSheet = document.styleSheets[0];
  styleSheet.insertRule(
    `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `,
    styleSheet.cssRules.length
  );
}

export default Membership;
